import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSocket, isMockMode } from '@/lib/socket';

/**
 * Thin status pill that surfaces socket.io connection state.
 *
 * Discord users come from an app that's loud about reconnecting — a yellow
 * "Reconnecting…" bar appears within ~2s of any drop. Without that feedback
 * Bunker just looks broken (messages stop arriving, voice goes silent, no
 * explanation). This component fixes the silence cheaply.
 *
 * States:
 *   - connected → render nothing (no chrome stolen during normal use)
 *   - reconnecting → warning-tinted pill, "Восстанавливаем соединение…"
 *   - offline → danger-tinted pill, "Нет связи с сервером"
 *
 * Mounts above the InfoBar so it's the FIRST thing in the main column the
 * user sees — does not collide with the TopBar guild rail.
 */
type Status = 'connected' | 'reconnecting' | 'offline';
export const RECONNECT_ACTION_DELAY_MS = 10_000;

export function ConnectionStatus() {
  const { t } = useTranslation();
  // Read the live state directly from the socket so we don't render a stale
  // "Reconnecting…" pill if the component mounts mid-reconnect.
  const [status, setStatus] = useState<Status>(() => {
    // `?mock=full` never connects by design: there is nothing to restore, and
    // a permanent «Восстанавливаем соединение» only hides the screen being
    // looked at (design review, the UI gallery).
    if (isMockMode()) return 'connected';
    try {
      return getSocket().connected ? 'connected' : 'reconnecting';
    } catch {
      return 'reconnecting';
    }
  });

  useEffect(() => {
    if (isMockMode()) return;
    const s = getSocket();
    const onConnect = () => setStatus('connected');
    const onDisconnect = (reason: unknown) => {
      // 'io server disconnect' = the server kicked us, won't auto-reconnect.
      // Anything else is a transient drop and socket.io will retry.
      setStatus(reason === 'io server disconnect' ? 'offline' : 'reconnecting');
    };
    const onReconnectAttempt = () => setStatus('reconnecting');
    const onReconnectFailed = () => setStatus('offline');

    s.on('connect', onConnect);
    s.on('disconnect', onDisconnect);
    s.io.on('reconnect_attempt', onReconnectAttempt);
    s.io.on('reconnect_failed', onReconnectFailed);

    return () => {
      s.off('connect', onConnect);
      s.off('disconnect', onDisconnect);
      s.io.off('reconnect_attempt', onReconnectAttempt);
      s.io.off('reconnect_failed', onReconnectFailed);
    };
  }, []);

  useEffect(() => {
    if (status !== 'reconnecting') return;
    const timer = window.setTimeout(() => setStatus('offline'), RECONNECT_ACTION_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  if (status === 'connected') return null;

  const isOffline = status === 'offline';
  // Minimal text-only status row — no pill chrome, no glass background,
  // no ring. Just animated dots + label, in the chrome bg. The shimmer
  // now lives ON the text itself (background-clip:text + moving gradient)
  // so the words read as "alive" without a banner that screams emergency.
  //
  // Offline gets a steady red dot + dim label — the situation is static,
  // visual motion would lie about progress.
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex justify-center px-3 pt-1 pb-0.5"
    >
      <span className="inline-flex items-center gap-2">
        {isOffline ? (
          <span
            aria-hidden
            className="inline-block h-1.5 w-1.5 rounded-full bg-danger"
          />
        ) : (
          <span aria-hidden className="inline-flex items-center gap-[3px]">
            <span className="connection-dot connection-dot-1 h-1 w-1 rounded-full bg-warning" />
            <span className="connection-dot connection-dot-2 h-1 w-1 rounded-full bg-warning" />
            <span className="connection-dot connection-dot-3 h-1 w-1 rounded-full bg-warning" />
          </span>
        )}
        <span
          className={
            'font-medium text-[10px] tracking-[0.08em] ' +
            (isOffline ? 'text-danger-ink/80' : 'connection-shimmer-text')
          }
        >
          {isOffline ? t('connection.offline') : t('connection.reconnecting')}
        </span>
        {isOffline && (
          <>
            <span className="text-[10px] tabular-nums text-ink-muted">
              {t('connection.diagnostic', { code: 'WS-01' })}
            </span>
            <button
              type="button"
              onClick={() => {
                setStatus('reconnecting');
                getSocket().connect();
              }}
              className="rounded-md bg-danger/15 px-2 py-1 text-[11px] font-medium text-danger-ink transition-colors hover:bg-danger/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {t('connection.retry')}
            </button>
          </>
        )}
      </span>
    </div>
  );
}
